import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CaretDown, Question, Envelope } from "@phosphor-icons/react";
import SEOHead from "../components/SEOHead";

const FAQItem = ({ faq, index, openIndexes, toggleIndex }) => {
  const isOpen = openIndexes.includes(index);

  return (
    <div
      className={`group bg-white/5 rounded-2xl border transition-all duration-300 overflow-hidden ${
        isOpen ? 'border-lumina-violet/40 shadow-lg shadow-lumina-violet/10' : 'border-white/10 hover:border-lumina-violet/30'
      }`}
      data-testid={`faq-${index}`}
    >
      <button
        className="w-full p-5 md:p-6 text-left flex items-center justify-between hover:bg-white/[0.03] transition-colors"
        onClick={() => toggleIndex(index)}
      >
        <span className="text-base md:text-lg font-medium text-white pr-4">{faq.question}</span>
        <div className={`w-9 h-9 md:w-10 md:h-10 bg-lumina-violet/15 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
          isOpen ? "rotate-180 bg-lumina-violet/30" : ""
        }`}>
          <CaretDown size={18} weight="bold" className="text-lumina-violet" />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${
        isOpen ? "max-h-[500px]" : "max-h-0"
      }`}>
        <p className="px-5 md:px-6 pb-5 md:pb-6 text-gray-400 leading-relaxed text-sm md:text-base">{faq.answer}</p>
      </div>
    </div>
  );
};

const FAQPage = () => {
  const [openIndexes, setOpenIndexes] = useState([]);
  const { t } = useTranslation();
  const faqs = t("faqPage.questions", { returnObjects: true }) || [];

  const toggleIndex = (index) => {
    setOpenIndexes((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      const next = [...prev, index];
      if (next.length > 5) next.shift();
      return next;
    });
  };

  return (
    <main role="main" aria-label={t("faqPage.seo.title")} className="min-h-screen bg-lumina-dark pt-24 overflow-x-hidden">
      <SEOHead title={t("faqPage.seo.title")} description={t("faqPage.seo.description")} path="/faq" />

      <style>{`
        .faq-bg {
          background: #030305;
          position: absolute;
          inset: 0;
        }
        .faq-bg::after {
          content: "";
          position: absolute;
          top: 20%;
          left: 50%;
          width: 800px;
          height: 800px;
          transform: translate(-50%, -50%);
          background: radial-gradient(circle, rgba(60,20,120,0.12) 0%, rgba(20,5,60,0.04) 40%, transparent 65%);
          pointer-events: none;
        }
      `}</style>

      <div className="faq-bg" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-violet-400/40 bg-violet-500/[0.1] mb-8"
            style={{ boxShadow: '0 0 20px rgba(139,92,246,0.2), inset 0 0 15px rgba(139,92,246,0.05)' }}>
            <Question size={20} weight="duotone" className="text-violet-400" style={{ filter: 'drop-shadow(0 0 8px rgba(139,92,246,0.8))' }} />
            <span className="text-violet-300 text-sm sm:text-base font-bold tracking-[0.12em] uppercase"
              style={{ textShadow: '0 0 15px rgba(139,92,246,0.6)' }}>
              FAQ
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight tracking-tight text-white mb-5">
            {t("faqPage.title")}
          </h1>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            {t("faqPage.subtitle")}
          </p>
        </div>

        {/* Questions */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <FAQItem key={faq.question} faq={faq} index={index} openIndexes={openIndexes} toggleIndex={toggleIndex} />
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <p className="text-white text-lg md:text-xl font-semibold mb-3">{t("faqPage.contactTitle")}</p>
          <p className="text-gray-400 text-sm md:text-base mb-6">{t("faqPage.contactSubtitle")}</p>
          <a
            href="mailto:support@luminawallet.org"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl text-white font-semibold text-base border border-violet-500/30 bg-transparent hover:bg-violet-500/[0.06] transition-all duration-300"
          >
            <Envelope size={18} weight="duotone" />
            <span>support@luminawallet.org</span>
          </a>
        </div>
      </div>
    </main>
  );
};

export default FAQPage;
